import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { fetchTrip } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { DestinationBadge } from "../components/DestinationBadge";
import { Button } from "../components/ui/button";
import { Calendar, Plane, Hotel, MapPin, Clock, User, Phone, Mail, RefreshCw, MessageCircleMore, Car } from "lucide-react";
import { format } from "date-fns";

const WA_NUMBER = "+52 81 1721 9585";
const WA_LINK = "https://wa.me/528117219585?text=Hi%2C%20I%20just%20submitted%20my%20travel%20details%20to%20Carrey%20Transportation%20and%20wanted%20to%20confirm%20a%20few%20things.";

export default function TravelerStatus() {
  const navigate = useNavigate();
  const { activeTripId } = useRole();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!activeTripId) { setLoading(false); return; }
    try {
      const t = await fetchTrip(activeTripId);
      setTrip(t);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTripId]);

  if (loading) return <div className="p-8 text-zinc-400 text-center" data-testid="status-loading">Loading your trip...</div>;

  if (!activeTripId || !trip) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6">
        <div className="glass-card p-8 max-w-md text-center" data-testid="no-trip-state">
          <Plane className="h-12 w-12 text-[#F07D00] mx-auto mb-4 opacity-80" />
          <h2 className="font-heading text-xl font-semibold text-white mb-2">No active trip</h2>
          <p className="text-sm text-zinc-400 mb-6">Submit a travel record to track its status here.</p>
          <Button onClick={() => navigate("/traveler/submit")} className="brand-btn" data-testid="go-to-submit-button">
            Submit a trip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="traveler-status-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <p className="label-eyebrow mb-2">My Travel Record</p>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Hello, {trip.full_name.split(" ")[0]}</h1>
            <Button variant="ghost" size="sm" onClick={load} className="text-zinc-400 hover:text-white hover:bg-white/5" data-testid="refresh-button">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* HUGE WhatsApp CTA */}
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-cta"
          className="group relative block overflow-hidden rounded-3xl border-2 border-emerald-500/40 hover:border-emerald-400 transition-all shadow-[0_0_60px_rgba(16,185,129,0.25)] hover:shadow-[0_0_80px_rgba(16,185,129,0.45)]"
        >
          {/* Background layers */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_50%)]" />
          <div className="absolute inset-0 grid-bg opacity-30" />

          <div className="relative p-7 sm:p-10 lg:p-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white/15 backdrop-blur-md border-2 border-white/30 flex items-center justify-center flex-shrink-0 shadow-xl animate-slow-pulse">
                <MessageCircleMore className="h-7 w-7 sm:h-8 sm:w-8 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-emerald-200">Action required · WhatsApp</span>
                </div>
                <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                  Tap below to start your chat now.
                </h2>
              </div>
            </div>

            <p className="text-sm sm:text-lg text-emerald-50/90 leading-relaxed mb-7 max-w-2xl">
              Your trip is in the queue. <strong className="text-white">All confirmations, driver assignments, and updates happen on WhatsApp.</strong> Tap the button to open WhatsApp and send your first message — your coordinator is standing by.
            </p>

            {/* Giant call-to-action button */}
            <div
              data-testid="whatsapp-button"
              className="inline-flex items-center gap-3 sm:gap-4 px-6 sm:px-9 py-4 sm:py-5 rounded-2xl bg-white text-emerald-700 font-bold text-base sm:text-xl shadow-2xl group-hover:scale-[1.02] group-hover:shadow-[0_20px_60px_rgba(0,0,0,0.4)] transition-all"
            >
              <MessageCircleMore className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2.5} />
              <span className="font-mono tracking-tight" data-testid="whatsapp-number">{WA_NUMBER}</span>
              <span className="hidden sm:inline text-emerald-600">→ Open WhatsApp</span>
            </div>

            <p className="mt-5 text-xs sm:text-sm text-emerald-100/70">
              Opens WhatsApp directly. A pre-written message is ready — just review and send.
            </p>
          </div>
        </a>

        {/* Hero summary */}
        <div className="glass-card p-6 sm:p-8 relative overflow-hidden" data-testid="trip-summary-card">
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl opacity-20"
            style={{ background: trip.destination === "Monterrey" ? "#10B981" : "#F59E0B" }} />
          <div className="relative flex flex-wrap items-center gap-3 mb-5">
            <DestinationBadge destination={trip.destination} size="lg" />
            <StatusBadge status={trip.status} />
          </div>
          <h2 className="font-heading text-2xl font-medium text-white mb-1">{trip.purpose || "Trip to " + trip.destination}</h2>
          <div className="flex items-center gap-4 text-sm text-zinc-400 flex-wrap">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {format(new Date(trip.arrival_date), "MMM d")} → {format(new Date(trip.departure_date), "MMM d, yyyy")}</span>
            {trip.flight_number && <span className="flex items-center gap-1.5"><Plane className="h-4 w-4" /> {trip.airline} {trip.flight_number}</span>}
          </div>
        </div>

        {/* Detail grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailCard icon={User} title="Personal">
            <Row label="Name" value={trip.full_name} />
            <Row label="Email" value={trip.email} icon={Mail} />
            <Row label="Phone" value={trip.phone || "—"} icon={Phone} />
          </DetailCard>

          <DetailCard icon={Plane} title="Flight">
            <Row label="Airline" value={trip.airline || "—"} />
            <Row label="Flight #" value={trip.flight_number || "—"} />
            <Row label="Origin" value={trip.origin_city || "—"} />
          </DetailCard>

          <DetailCard icon={Hotel} title="Lodging">
            <Row label="Property" value={trip.accommodation_name || "—"} />
            <Row label="Type" value={trip.accommodation_type || "—"} />
            <Row label="Address" value={trip.accommodation_address || "—"} />
          </DetailCard>

          <DetailCard icon={MapPin} title="Schedule">
            <Row label="Arrival" value={`${format(new Date(trip.arrival_date), "MMM d")} ${trip.arrival_time || ""}`} />
            <Row label="Departure" value={`${format(new Date(trip.departure_date), "MMM d")} ${trip.departure_time || ""}`} />
            <Row label="Primary" value={trip.primary_location || "—"} />
          </DetailCard>

          {trip.driver_name && (
            <DetailCard icon={Car} title="Your driver">
              <Row label="Name" value={trip.driver_name} />
              <Row label="Phone" value={trip.driver_phone || "—"} icon={Phone} />
              <Row label="Vehicle" value={trip.driver_vehicle || "—"} />
              <Row label="Plates" value={trip.driver_license_plate || "—"} />
              {trip.driver_assigned_at && (
                <Row label="Assigned" value={format(new Date(trip.driver_assigned_at), "MMM d · HH:mm")} icon={Clock} />
              )}
            </DetailCard>
          )}
        </div>

        {/* Bottom reminder */}
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-bottom-link"
          className="block text-center text-sm text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline pt-2"
        >
          Need anything? Message your coordinator on WhatsApp →
        </a>
      </div>
    </div>
  );
}

function DetailCard({ icon: Icon, title, children }) {
  return (
    <div className="glass-card p-5" data-testid={`detail-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-[#F07D00]" />
        <h3 className="font-heading text-sm font-semibold tracking-wider uppercase text-zinc-300">{title}</h3>
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-zinc-500 min-w-[72px] text-xs uppercase tracking-wider mt-0.5">{label}</span>
      <span className="text-white flex-1 break-words flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3 text-zinc-500" />} {value}
      </span>
    </div>
  );
}
